import React, {useCallback, useRef, useState} from 'react'

import {Button, Variant} from '@vio/atlas/Button'
import {Popover, PopoverPlacement} from '@vio/atlas/Popover'
import {ShowPasswordlessLoginPayload} from '@vio/core/src/auth/types/Auth'
import {User} from '@vio/core/src/auth/types/Cognito'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'

import {AuthenticatedMenuItems} from '../AuthenticatedMenuItems'
import {SignInButton} from '../SignInButton'
import {UserAvatar} from '../UserAvatar'

interface Props {
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Callback fired when the user tries to sign in */
  onSignIn: ({
    sourceComponent,
    flow
  }: {
    sourceComponent: ShowPasswordlessLoginPayload['sourceComponent']
    flow: ShowPasswordlessLoginPayload['flow']
  }) => void
  /** User object with metadata such as profile picture, name etc. */
  user: User | null | undefined
  /** Callback fired when the user tries to log out */
  logOut?: () => void
  /** Url of users account page */
  accountUrl?: string
  /** Url of users alerts page */
  alertsUrl?: string
  /** Url of users bookings page */
  myBookingsUrl?: string
  /** Slot for the Get The App link */
  getTheAppSlot?: React.ReactNode
  /** Slot above the menu items, for the brand's loyalty program values */
  programSlot?: React.ReactNode
  /** Button variant */
  buttonVariant?: Variant
  /** Whether to hide the sign in button */
  isSignInHidden?: boolean
  /** Whether the page is right-to-left, so the menu hangs from the button's inner edge */
  isRtl?: boolean
}

export const HeaderUserWidget = ({
  isAuthenticated,
  onSignIn,
  user,
  logOut,
  accountUrl,
  alertsUrl,
  myBookingsUrl,
  getTheAppSlot = null,
  programSlot = null,
  buttonVariant = 'ghost',
  isSignInHidden = false,
  isRtl = false
}: Props) => {
  const [popoverOpen, setPopoverOpen] = useState(false)

  const togglePopover = useCallback(() => {
    setPopoverOpen(!popoverOpen)
  }, [popoverOpen, setPopoverOpen])

  const anchorRef = useRef(null)

  const handleLogOutClick = useCallback(() => {
    if (logOut) logOut()
  }, [logOut])

  if (isAuthenticated) {
    return (
      <div>
        <Button
          ref={anchorRef}
          variant={buttonVariant}
          dataId="UserWidget"
          className="fs-exclude"
          onClick={togglePopover}
          iconStart={<UserAvatar user={user || null} />}
        >
          <span>
            {user?.attributes?.given_name ? (
              user?.attributes?.given_name
            ) : (
              <FormattedMessageWrapper
                id="userWidget.myProfile"
                defaultMessage="My profile"
              />
            )}
          </span>
        </Button>
        <Popover
          isOpen={popoverOpen}
          anchorRef={anchorRef}
          onOutsideClick={togglePopover}
          placement={
            isRtl ? PopoverPlacement.BottomLeft : PopoverPlacement.BottomRight
          }
        >
          {programSlot}
          <AuthenticatedMenuItems
            accountUrl={accountUrl}
            alertsUrl={alertsUrl}
            myBookingsUrl={myBookingsUrl}
            getTheAppSlot={getTheAppSlot}
            onLogOut={handleLogOutClick}
            isAuthenticated
          />
        </Popover>
      </div>
    )
  }

  return !isSignInHidden ? (
    <SignInButton onSignIn={onSignIn} buttonVariant={buttonVariant} />
  ) : null
}
